import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Home, LogOut, Users, FileCheck2, Truck, Mail, ShieldCheck, Check, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const StatCard = ({ label, value }) => (
  <div className="border border-gold/15 bg-ink-surface p-6">
    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">{label}</p>
    <p className="font-serif text-4xl text-gold-gradient mt-3">{value}</p>
  </div>
);

const money = (amt, cur) => `${(amt / 100).toFixed(2)} ${(cur || "usd").toUpperCase()}`;

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [members, setMembers] = useState([]);

  const loadAll = async () => {
    try {
      const [s, g, o, i, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/genealogy"),
        api.get("/admin/orders"),
        api.get("/admin/inquiries"),
        api.get("/admin/members"),
      ]);
      setStats(s.data); setSubs(g.data); setOrders(o.data); setInquiries(i.data); setMembers(m.data);
    } catch (e) {
      toast.error("Failed to load admin data.");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const reviewSub = async (id, review_status) => {
    try {
      await api.patch(`/admin/genealogy/${id}`, { review_status });
      toast.success(`Submission ${review_status}.`);
      setSubs((prev) => prev.map((r) => (r.id === id ? { ...r, review_status } : r)));
      loadAll();
    } catch (e) { toast.error("Update failed."); }
  };

  const updateOrder = async (id, status, tracking) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status, tracking });
      toast.success("Order updated.");
      loadAll();
    } catch (e) { toast.error("Update failed."); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const badge = (s) => {
    const map = { pending: "text-[#c9a24d] border-[#c9a24d]/40", approved: "text-gold border-gold/50", rejected: "text-[#c07a7a] border-[#c07a7a]/40" };
    return `font-mono text-[9px] tracking-[0.14em] uppercase border px-2 py-1 ${map[s] || map.pending}`;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-gold/15 bg-ink-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={22} className="text-gold" />
            <div className="leading-none">
              <p className="font-serif text-lg tracking-[0.12em]">ADMIN BACK OFFICE</p>
              <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-gold/60 mt-1">Office of the Steward · {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="admin-home-btn" className="flex items-center gap-2 px-4 py-2.5 border border-gold/20 text-[#A19D98] hover:text-gold hover:border-gold/40 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors">
              <Home size={14} /> Site
            </button>
            <button onClick={doLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 px-4 py-2.5 border border-gold/20 text-[#A19D98] hover:text-gold hover:border-gold/40 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
              <StatCard label="Members" value={stats.members} />
              <StatCard label="Submissions" value={stats.submissions_total} />
              <StatCard label="Pending Review" value={stats.submissions_pending} />
              <StatCard label="ID Orders" value={stats.orders_total} />
              <StatCard label="Processing" value={stats.orders_processing} />
              <StatCard label="Inquiries" value={stats.inquiries} />
            </div>
          )}

          <Tabs defaultValue="submissions" className="w-full">
            <TabsList className="bg-ink-surface border border-gold/15 flex-wrap h-auto">
              {[["submissions", "Submissions", FileCheck2], ["orders", "ID Orders", Truck], ["inquiries", "Inquiries", Mail], ["members", "Members", Users]].map(([v, l, Icon]) => (
                <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-gold data-[state=active]:text-black font-mono text-[10px] tracking-[0.16em] uppercase gap-2">
                  <Icon size={13} /> {l}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Submissions */}
            <TabsContent value="submissions" className="mt-6">
              <div className="border border-gold/15 overflow-x-auto" data-testid="admin-submissions">
                <table className="w-full">
                  <thead><tr className="bg-ink-surface">{["Title", "Submitter", "Lineage", "Block", "Review", "Actions"].map((h) => <th key={h} className="text-left font-mono text-[10px] tracking-[0.16em] uppercase text-gold/60 px-5 py-4 border-b border-gold/15">{h}</th>)}</tr></thead>
                  <tbody>
                    {subs.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center font-sans text-sm text-[#7a766f]">No submissions.</td></tr>}
                    {subs.map((r) => (
                      <tr key={r.id} className="border-b border-gold/10">
                        <td className="px-5 py-4 font-sans text-sm text-[#F5F0EB]">{r.title}</td>
                        <td className="px-5 py-4 font-sans text-xs text-[#A19D98]">{r.submitter}<br /><span className="text-[#6b6862]">{r.submitter_email}</span></td>
                        <td className="px-5 py-4 font-sans text-xs text-[#A19D98]">{r.lineage}</td>
                        <td className="px-5 py-4 font-mono text-xs text-gold-text">#{r.block_number}</td>
                        <td className="px-5 py-4"><span className={badge(r.review_status)}>{r.review_status || "pending"}</span></td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => reviewSub(r.id, "approved")} data-testid={`approve-${r.id}`} title="Approve" className="p-2 border border-gold/30 text-gold hover:bg-gold hover:text-black transition-colors"><Check size={14} /></button>
                            <button onClick={() => reviewSub(r.id, "rejected")} data-testid={`reject-${r.id}`} title="Reject" className="p-2 border border-[#c07a7a]/40 text-[#c07a7a] hover:bg-[#c07a7a] hover:text-black transition-colors"><X size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Orders */}
            <TabsContent value="orders" className="mt-6">
              <div className="border border-gold/15 overflow-x-auto" data-testid="admin-orders">
                <table className="w-full">
                  <thead><tr className="bg-ink-surface">{["Member", "Type", "Amount", "Ship To", "Status", "Tracking", ""].map((h) => <th key={h} className="text-left font-mono text-[10px] tracking-[0.16em] uppercase text-gold/60 px-4 py-4 border-b border-gold/15">{h}</th>)}</tr></thead>
                  <tbody>
                    {orders.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center font-sans text-sm text-[#7a766f]">No orders yet.</td></tr>}
                    {orders.map((o) => <OrderRow key={o.id} order={o} onSave={updateOrder} />)}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Inquiries */}
            <TabsContent value="inquiries" className="mt-6">
              <div className="space-y-4" data-testid="admin-inquiries">
                {inquiries.length === 0 && <p className="font-sans text-sm text-[#7a766f]">No inquiries.</p>}
                {inquiries.map((q) => (
                  <div key={q.id} className="border border-gold/15 bg-ink-surface p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-serif text-lg text-[#F5F0EB]">{q.name} <span className="font-sans text-xs text-[#A19D98]">· {q.email}</span></p>
                        <p className="font-mono text-[9px] tracking-[0.18em] uppercase text-gold/60 mt-1">{q.category}{q.organization ? ` · ${q.organization}` : ""}</p>
                      </div>
                      <a href={`mailto:${q.email}`} className="font-mono text-[10px] tracking-[0.16em] uppercase text-gold border border-gold/30 px-3 py-2 hover:bg-gold hover:text-black transition-colors">Reply</a>
                    </div>
                    <p className="font-sans text-sm text-[#C9C4BD] leading-relaxed mt-4">{q.message}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Members */}
            <TabsContent value="members" className="mt-6">
              <div className="border border-gold/15 overflow-x-auto" data-testid="admin-members">
                <table className="w-full">
                  <thead><tr className="bg-ink-surface">{["Name", "Email", "Credential", "Records", "Role"].map((h) => <th key={h} className="text-left font-mono text-[10px] tracking-[0.16em] uppercase text-gold/60 px-5 py-4 border-b border-gold/15">{h}</th>)}</tr></thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.user_id} className="border-b border-gold/10">
                        <td className="px-5 py-4 font-sans text-sm text-[#F5F0EB]">{m.name}</td>
                        <td className="px-5 py-4 font-sans text-xs text-[#A19D98]">{m.email}</td>
                        <td className="px-5 py-4 font-mono text-xs text-gold-text">{m.credential_id}</td>
                        <td className="px-5 py-4 font-mono text-sm text-[#F5F0EB]">{m.records}</td>
                        <td className="px-5 py-4"><span className="font-mono text-[9px] tracking-[0.14em] uppercase text-gold">{m.is_admin ? "Admin" : "Member"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}

function OrderRow({ order, onSave }) {
  const [status, setStatus] = useState(order.status);
  const [tracking, setTracking] = useState(order.tracking || "");
  const addr = order.shipping;
  return (
    <tr className="border-b border-gold/10 align-top">
      <td className="px-4 py-4 font-sans text-sm text-[#F5F0EB]">{order.member}<br /><span className="text-[10px] text-[#6b6862]">{order.member_email}</span></td>
      <td className="px-4 py-4 font-sans text-xs text-[#A19D98] capitalize">{order.card_type}</td>
      <td className="px-4 py-4 font-mono text-xs text-gold-text">{money(order.amount, order.currency)}</td>
      <td className="px-4 py-4 font-sans text-[11px] text-[#A19D98] max-w-[160px]">
        {addr ? `${addr.line1 || ""} ${addr.city || ""} ${addr.state || ""} ${addr.postal_code || ""} ${addr.country || ""}` : <span className="text-[#6b6862]">—</span>}
      </td>
      <td className="px-4 py-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`order-status-${order.id}`} className="w-32 bg-transparent border-gold/25 text-[#F5F0EB] font-mono text-[10px] uppercase h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-ink-surface border-gold/20 text-[#F5F0EB]">
            {["processing", "shipped", "delivered", "cancelled"].map((s) => <SelectItem key={s} value={s} className="font-mono text-xs capitalize focus:bg-gold/10 focus:text-gold">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-4">
        <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking #" data-testid={`order-tracking-${order.id}`} className="w-28 bg-transparent border-b border-gold/25 focus:border-gold outline-none py-1 font-mono text-xs text-[#F5F0EB] placeholder:text-[#5f5c57]" />
      </td>
      <td className="px-4 py-4">
        <button onClick={() => onSave(order.id, status, tracking)} data-testid={`save-order-${order.id}`} className="font-mono text-[10px] tracking-[0.14em] uppercase text-gold border border-gold/30 px-3 py-2 hover:bg-gold hover:text-black transition-colors">Save</button>
      </td>
    </tr>
  );
}
